import {
  calculateBMIFromObservations,
  CM_PER_INCH,
  KG_PER_LB,
  observationHeightToCm,
  observationWeightToKg,
} from '@bariendo/lib/bmi';
import { formatBmi } from '@bariendo/lib/insurance-eligibility';
import { BODY_HEIGHT_LOINC_CODING, BODY_WEIGHT_LOINC_CODING } from '@bariendo/lib/codings';
import { Observation, Patient, Reference } from '@medplum/fhirtypes';
import { cmToFeetInches, kgToLbs } from '@/services/patient';

export interface BmiDisplay {
  /** One decimal, truncated, e.g. "37.4". */
  value: string;
  /** Unrounded, for the likely-eligibility decision, which signup also makes on the exact value. */
  exact: number;
  /** The inputs it came from, in the units the navigator says: "232 lb" and `5'6"`. */
  weight: string;
  height: string;
}

/**
 * BMI for the qualification beat, with the measurements it came from.
 *
 * The number comes from `calculateBMIFromObservations`, the same call the BMI panel
 * and the patient list use. Shown
 * truncated rather than rounded, as the likely-eligibility line decides
 * (BAR-2991): a computed 39.95 reads 39.9 here and 40.0 in the panel, which rounds. Returns undefined when either measurement is
 * missing, so the card prompts the navigator rather than showing a wrong number.
 */
export function describeBmi(weight: Observation | undefined, height: Observation | undefined): BmiDisplay | undefined {
  const bmi = calculateBMIFromObservations(weight, height);
  const kg = observationWeightToKg(weight);
  const cm = observationHeightToCm(height);
  if (bmi === undefined || kg === undefined || cm === undefined) return undefined;

  return { value: formatBmi(bmi), exact: bmi, weight: `${kgToLbs(kg)} lb`, height: cmToFeetInches(cm) };
}

/** The qualification row: weight, height and BMI in the order they are read out, e.g. `232 lb · 5'6" · BMI 33.4`. */
export function formatWeightHeightBmi(bmi: BmiDisplay): string {
  return `${bmi.weight} · ${bmi.height} · BMI ${bmi.value}`;
}

/* -------------------------------------------------------------------------
   Weight and height as the navigator types them (BAR-2999)
   ------------------------------------------------------------------------- */

/** What the three boxes hold, as typed. */
export interface EnteredMeasurements {
  lb: string;
  ft: string;
  inches: string;
}

/** Plausible adult ranges: anything outside is a typo, not a measurement to write. */
const WEIGHT_LB = { min: 50, max: 1000 };
const HEIGHT_FT = { min: 3, max: 8 };
const HEIGHT_IN = { min: 0, max: 11 };

const VITAL_SIGNS_CATEGORY = {
  coding: [
    {
      system: 'http://terminology.hl7.org/CodeSystem/observation-category',
      code: 'vital-signs',
      display: 'Vital Signs',
    },
  ],
};

function parseNumber(text: string, range: { min: number; max: number }): number | undefined {
  if (!/^\d+(\.\d+)?$/.test(text.trim())) return undefined;
  const value = Number(text);
  return value >= range.min && value <= range.max ? value : undefined;
}

/** The weight in kilograms the box describes, or undefined for nothing usable. */
export function enteredWeightKg(lb: string): number | undefined {
  const pounds = parseNumber(lb, WEIGHT_LB);
  // Unrounded, as bariendo-be writes weights since BAR-3018, so the live BMI is the BMI the write gives.
  return pounds === undefined ? undefined : pounds * KG_PER_LB;
}

/**
 * The height in centimetres the two boxes describe, or undefined for nothing
 * usable. An empty inches box is incomplete, not 0: no BMI, nothing written.
 * An explicit 0 is a height.
 */
export function enteredHeightCm(ft: string, inches: string): number | undefined {
  const feet = parseNumber(ft, HEIGHT_FT);
  const extra = parseNumber(inches, HEIGHT_IN);
  if (feet === undefined || extra === undefined) return undefined;
  return (feet * 12 + extra) * CM_PER_INCH;
}

/** A body weight Observation, kg in UCUM and unrounded, as bariendo-be writes them (BAR-3018). Unsaved until created. */
export function weightObservation(kg: number, subject?: Reference<Patient>): Observation {
  return {
    resourceType: 'Observation',
    status: 'final',
    category: [VITAL_SIGNS_CATEGORY],
    code: { coding: [BODY_WEIGHT_LOINC_CODING], text: 'Body weight' },
    ...(subject ? { subject } : {}),
    valueQuantity: { value: kg, unit: 'kg', system: 'http://unitsofmeasure.org', code: 'kg' },
  };
}

/** A body height Observation, cm in UCUM. Unsaved until created. */
export function heightObservation(cm: number, subject?: Reference<Patient>): Observation {
  return {
    resourceType: 'Observation',
    status: 'final',
    category: [VITAL_SIGNS_CATEGORY],
    code: { coding: [BODY_HEIGHT_LOINC_CODING], text: 'Body height' },
    ...(subject ? { subject } : {}),
    valueQuantity: { value: cm, unit: 'cm', system: 'http://unitsofmeasure.org', code: 'cm' },
  };
}

/** The boxes prefilled from the latest Observations: whole pounds, feet and inches. Empty where none. */
export function enteredFromObservations(
  weight: Observation | undefined,
  height: Observation | undefined
): EnteredMeasurements {
  const kg = observationWeightToKg(weight);
  const cm = observationHeightToCm(height);
  let ft = '';
  let inches = '';
  if (cm !== undefined) {
    const total = Math.round(cm / CM_PER_INCH);
    ft = String(Math.floor(total / 12));
    inches = String(total % 12);
  }
  return { lb: kg === undefined ? '' : String(kgToLbs(kg)), ft, inches };
}
